import { useState, useEffect, useCallback } from 'react';
import { prRepository } from '../repositories/pr.repository';
import { masterDataRepository } from '../repositories/master-data.repository';
import { shouldMakeAPICall, onNetworkStatusChange } from '../utils/network';

/**
 * Hook for accessing offline PR data
 */
export const useOfflinePRs = (
  authToken,
  userToken,
  orgId,
  accessType = "Buyer",
  options = {}
) => {
  const [prs, setPRs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const skipApiSync = Boolean(options?.skipApiSync);

  const loadPRs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Always load from cache first
      const cachedPRs = await prRepository.getAllFromCache();
      const optimisticCount = cachedPRs.filter(pr => pr._pendingSync || pr._lastUpdated).length;
      console.log(`📦 Loaded ${cachedPRs.length} PRs from IndexedDB (${optimisticCount} with optimistic updates)`);
      
      if (optimisticCount > 0) {
        console.log('📱 Optimistic updates found:', cachedPRs
          .filter(pr => pr._pendingSync || pr._lastUpdated)
          .map(pr => ({ pr_code: pr.pr_code, pr_token: pr.pr_token, request_reason: pr.request_reason?.substring(0, 30) }))
        );
      }
      
      setPRs(cachedPRs);

      // Try to sync if online - use combined API for better data
      // NOTE: syncCombinedPRsFromAPI now preserves optimistic updates automatically
      if (!skipApiSync && shouldMakeAPICall(authToken, userToken) && authToken && userToken && orgId) {
        console.log('🌐 Online - syncing from API (will preserve optimistic updates)');
        // Try combined API first (has all items and full details)
        // This will preserve PRs with _pendingSync flag
        const syncedPRs = await prRepository.syncCombinedPRsFromAPI(
          authToken,
          userToken,
          orgId,
          String(accessType || "").toLowerCase() === "user"
        );
        if (syncedPRs && syncedPRs.length > 0) {
          const syncedOptimistic = syncedPRs.filter(pr => pr._pendingSync || pr._lastUpdated).length;
          console.log(`✅ Synced ${syncedPRs.length} PRs (${syncedOptimistic} optimistic updates preserved)`);
          setPRs(syncedPRs);
        } else {
          // Fallback to regular API if combined fails (also preserves optimistic updates)
          const fallbackPRs = await prRepository.syncFromAPI(
            authToken,
            userToken,
            orgId,
            String(accessType || "").toLowerCase() === "user"
          );
          const fallbackOptimistic = fallbackPRs.filter(pr => pr._pendingSync || pr._lastUpdated).length;
          console.log(`✅ Fallback synced ${fallbackPRs.length} PRs (${fallbackOptimistic} optimistic updates preserved)`);
          setPRs(fallbackPRs);
        }
      } else {
        console.log('📴 Offline - using cached data only');
      }
    } catch (err) {
      console.error('Error loading PRs:', err);
      setError(err);
      // Still show cached data even on error
      const cachedPRs = await prRepository.getAllFromCache();
      setPRs(cachedPRs);
    } finally {
      setLoading(false);
    }
  }, [authToken, userToken, orgId, accessType]);

  useEffect(() => {
    loadPRs();

    // Listen for network changes
    const cleanup = onNetworkStatusChange(
      () => {
        // Online - trigger sync
        if (shouldMakeAPICall(authToken, userToken) && authToken && userToken && orgId) {
          loadPRs();
        }
      },
      () => {
        // Offline - just use cache
        console.log('📴 Offline mode - using cached data');
      }
    );

    return cleanup;
  }, [loadPRs, authToken, userToken, orgId]);

  return { prs, loading, error, refetch: loadPRs };
};

/**
 * Hook for accessing all master data
 */
export const useOfflineMasterData = (
  authToken,
  userToken
) => {
  const [costCenters, setCostCenters] = useState([]);
  const [glCodes, setGLCodes] = useState([]);
  const [projectCodes, setProjectCodes] = useState([]);
  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [users, setUsers] = useState([]);
  const [prCodes, setPRCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);

      // Load all from cache first
      const [
        cachedCostCenters,
        cachedGLCodes,
        cachedProjectCodes,
        cachedItems,
        cachedDepartments,
        cachedVendors,
        cachedUsers,
        cachedPRCodes,
      ] = await Promise.all([
        masterDataRepository.getCostCentersFromCache(),
        masterDataRepository.getGLCodesFromCache(),
        masterDataRepository.getProjectCodesFromCache(),
        masterDataRepository.getItemsFromCache(),
        masterDataRepository.getDepartmentsFromCache(),
        masterDataRepository.getVendorsFromCache(),
        masterDataRepository.getUsersFromCache(),
        masterDataRepository.getPRCodesFromCache(),
      ]);

      setCostCenters(cachedCostCenters);
      setGLCodes(cachedGLCodes);
      setProjectCodes(cachedProjectCodes);
      setItems(cachedItems);
      setDepartments(cachedDepartments);
      setVendors(cachedVendors);
      setUsers(cachedUsers);
      setPRCodes(cachedPRCodes);

      // Sync if online
      if (shouldMakeAPICall(authToken, userToken) && authToken && userToken) {
        const [
          syncedCostCenters,
          syncedGLCodes,
          syncedProjectCodes,
          syncedItems,
          syncedDepartments,
          syncedVendors,
          syncedUsers,
        ] = await Promise.allSettled([
          masterDataRepository.syncCostCenters(authToken, userToken),
          masterDataRepository.syncGLCodes(authToken, userToken),
          masterDataRepository.syncProjectCodes(authToken, userToken),
          masterDataRepository.syncItems(authToken, userToken),
          masterDataRepository.syncDepartments(authToken, userToken),
          masterDataRepository.syncVendors(authToken, userToken),
          masterDataRepository.syncUsers(authToken, userToken),
        ]);

        if (syncedCostCenters.status === 'fulfilled') {
          setCostCenters(syncedCostCenters.value);
        }
        if (syncedGLCodes.status === 'fulfilled') {
          setGLCodes(syncedGLCodes.value);
        }
        if (syncedProjectCodes.status === 'fulfilled') {
          setProjectCodes(syncedProjectCodes.value);
        }
        if (syncedItems.status === 'fulfilled') {
          setItems(syncedItems.value);
        }
        if (syncedDepartments.status === 'fulfilled') {
          setDepartments(syncedDepartments.value);
        }
        if (syncedVendors.status === 'fulfilled') {
          setVendors(syncedVendors.value);
        }
        if (syncedUsers.status === 'fulfilled') {
          setUsers(syncedUsers.value);
        }

        // PR codes don't need userToken
        if (authToken) {
          const syncedPRCodes = await masterDataRepository.syncPRCodes(authToken);
          setPRCodes(syncedPRCodes);
        }
      }
    } catch (error) {
      console.error('Error loading master data:', error);
    } finally {
      setLoading(false);
    }
  }, [authToken, userToken]);

  useEffect(() => {
    loadAll();

    // Listen for network changes
    const cleanup = onNetworkStatusChange(
      () => {
        if (shouldMakeAPICall(authToken, userToken) && authToken && userToken) {
          loadAll();
        }
      },
      () => {
        console.log('📴 Offline mode - using cached master data');
      }
    );

    return cleanup;
  }, [loadAll, authToken, userToken]);

  return {
    costCenters,
    glCodes,
    projectCodes,
    items,
    departments,
    vendors,
    users,
    prCodes,
    loading,
    refetch: loadAll,
  };
};

/**
 * Hook for initial data load (all 9 APIs)
 */
export const useInitialDataLoad = (
  authToken,
  userToken,
  orgId,
  options = {}
) => {
  const prData = useOfflinePRs(authToken, userToken, orgId, "Buyer", options);
  const masterData = useOfflineMasterData(authToken, userToken);

  const loading = prData.loading || masterData.loading;

  return {
    ...prData,
    ...masterData,
    loading,
  };
};
